import { useEffect, useMemo } from 'react';
import { BubbleMenu, EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import styled from '@emotion/styled';
import { FormatBold, FormatItalic, FormatListBulleted, FormatListNumbered } from '@mui/icons-material';
import { uiTheme as t } from 'Components/ui';
import { createSlashMenu } from './slashMenu';

export interface RichTextEditorLabels {
  bold: string;
  italic: string;
  heading1: string;
  heading2: string;
  bulletList: string;
  orderedList: string;
  /** Bloque "texto normal" del menú de slash. */
  paragraph: string;
  /** aria-label de la toolbar fija. */
  toolbar: string;
  /** aria-label de la barra flotante sobre la selección. */
  selectionToolbar: string;
  /** aria-label del menú de slash. */
  slashMenu: string;
}

interface RichTextEditorProps {
  /** HTML actual del formulario (RHF es el dueño del valor). */
  value: string;
  placeholder?: string;
  labels: RichTextEditorLabels;
  /** Recibe el documento serializado a HTML en cada edición. */
  onChange: (html: string) => void;
  onBlur?: () => void;
}

// Mismo marco que tenía el TextArea: borde, radio y fondo de input.
const Frame = styled.div({
  border: `1px solid ${t.color.inputBorder}`,
  borderRadius: t.radius.sm,
  background: t.color.inputBg,
  transition: 'border-color 0.15s ease',
  '&:focus-within': { borderColor: t.color.primary },
});

const Toolbar = styled.div({
  display: 'flex',
  gap: 2,
  padding: '6px 8px',
  borderBottom: `1px solid ${t.color.rowBorder}`,
});

const ToolButton = styled.button<{ active: boolean }>(({ active }) => ({
  border: 'none',
  width: 28,
  height: 28,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 6,
  cursor: 'pointer',
  background: active ? t.color.countBg : 'transparent',
  color: active ? t.color.primary : t.color.muted,
  fontFamily: t.font,
  fontSize: 12,
  fontWeight: 700,
  transition: 'background 0.15s ease, color 0.15s ease',
  '&:hover': { color: active ? t.color.primary : t.color.title },
  '& .MuiSvgIcon-root': { fontSize: 18 },
}));

// Barra flotante sobre la selección: mismo lenguaje que la toolbar pero en
// oscuro elevado, para leerse como capa flotante sobre el texto.
const BubbleBar = styled.div({
  display: 'flex',
  gap: 2,
  padding: '4px 6px',
  borderRadius: t.radius.xs,
  background: t.color.title,
  boxShadow: t.shadow.cardHover,
});

const BubbleButton = styled.button<{ active: boolean }>(({ active }) => ({
  border: 'none',
  width: 28,
  height: 28,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 6,
  cursor: 'pointer',
  background: active ? 'rgba(255, 255, 255, 0.16)' : 'transparent',
  color: active ? t.color.surface : '#c3c9cf',
  fontFamily: t.font,
  fontSize: 12,
  fontWeight: 700,
  transition: 'background 0.15s ease, color 0.15s ease',
  '&:hover': { color: t.color.surface },
  '& .MuiSvgIcon-root': { fontSize: 18 },
}));

// El área editable calca la caja del TextArea que sustituye: misma altura
// mínima (4 filas) y scroll propio al crecer.
const EditArea = styled.div({
  position: 'relative',
  '& .ProseMirror': {
    boxSizing: 'border-box',
    minHeight: 106,
    maxHeight: 260,
    overflowY: 'auto',
    padding: '11px 13px',
    outline: 'none',
    fontFamily: t.font,
    fontSize: 14,
    lineHeight: 1.55,
    color: t.color.title,
  },
  '& .ProseMirror p': { margin: '0 0 6px' },
  '& .ProseMirror strong': { fontWeight: 700 } /* el * de GlobalStyle fuerza 400; el peso debe ser explícito */,
  '& .ProseMirror h1': {
    fontSize: 19,
    fontWeight: 700,
    margin: '10px 0 6px',
  } /* el * de GlobalStyle fuerza 400; el peso debe ser explícito */,
  '& .ProseMirror h2': {
    fontSize: 16,
    fontWeight: 700,
    margin: '8px 0 5px',
  } /* el * de GlobalStyle fuerza 400; el peso debe ser explícito */,
  '& .ProseMirror h1:first-of-type, & .ProseMirror h2:first-of-type': { marginTop: 0 },
  '& .ProseMirror ul, & .ProseMirror ol': { margin: '0 0 6px', paddingLeft: 22 },
  '& .ProseMirror > :last-child': { marginBottom: 0 },
  // Placeholder oficial de TipTap: la extensión solo marca el nodo vacío con
  // data-placeholder; pintarlo es cosa de este ::before.
  '& .ProseMirror p.is-editor-empty:first-of-type::before': {
    content: 'attr(data-placeholder)',
    float: 'left',
    height: 0,
    pointerEvents: 'none',
    color: t.color.disabled,
  },
});

// El editor solo ofrece lo que sus menús exponen: negritas, cursiva, h1/h2 y
// listas. Lo demás que StarterKit trae activo por default y ensuciaría el
// HTML (código, blockquote, tachado, línea horizontal) se apaga aquí. El menú
// de slash ofrece esos mismos bloques y nada más.
const buildExtensions = (placeholder: string, labels: RichTextEditorLabels) => [
  StarterKit.configure({
    heading: { levels: [1, 2] },
    blockquote: false,
    code: false,
    codeBlock: false,
    strike: false,
    horizontalRule: false,
  }),
  Placeholder.configure({ placeholder }),
  createSlashMenu({
    paragraph: labels.paragraph,
    heading1: labels.heading1,
    heading2: labels.heading2,
    bulletList: labels.bulletList,
    orderedList: labels.orderedList,
    menu: labels.slashMenu,
  }),
];

/** El documento vacío serializa `<p></p>`; el form quiere cadena vacía. */
const toHtml = (editor: Editor): string => (editor.isEmpty ? '' : editor.getHTML());

/**
 * Editor WYSIWYG de la descripción de una noticia (TipTap), con experiencia
 * estilo Notion: menú de slash ("/"), bubble menu sobre la selección y
 * placeholder oficial. El admin nunca ve sintaxis: formatea con los menús y
 * el valor viaja como HTML, que RichText sanitiza al renderear en el feed.
 * La toolbar fija se conserva como fallback accesible y descubrible.
 */
const RichTextEditor = ({ value, placeholder, labels, onChange, onBlur }: RichTextEditorProps) => {
  // Placeholder y labels no cambian durante la vida del editor; el memo evita
  // reconstruir las extensiones en cada render.
  const extensions = useMemo(
    () => buildExtensions(placeholder ?? '', labels),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const editor = useEditor({
    extensions,
    content: value,
    onUpdate: ({ editor: current }) => onChange(toHtml(current)),
    onBlur: () => onBlur?.(),
  });

  // RHF manda: si el form resetea (abrir el drawer, cambiar de noticia), el
  // documento se realinea. Cuando el cambio nació en el propio editor los
  // HTML coinciden y no se toca nada (ni bucle ni brinco de cursor).
  useEffect(() => {
    if (!editor) return;
    if (toHtml(editor) !== value) editor.commands.setContent(value, false);
  }, [editor, value]);

  if (!editor) return null;

  const controls = [
    {
      label: labels.bold,
      active: editor.isActive('bold'),
      content: <FormatBold />,
      run: () => editor.chain().focus().toggleBold().run(),
    },
    {
      label: labels.italic,
      active: editor.isActive('italic'),
      content: <FormatItalic />,
      run: () => editor.chain().focus().toggleItalic().run(),
    },
    {
      label: labels.heading1,
      active: editor.isActive('heading', { level: 1 }),
      content: 'H1',
      run: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
    },
    {
      label: labels.heading2,
      active: editor.isActive('heading', { level: 2 }),
      content: 'H2',
      run: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
    },
    {
      label: labels.bulletList,
      active: editor.isActive('bulletList'),
      content: <FormatListBulleted />,
      run: () => editor.chain().focus().toggleBulletList().run(),
    },
    {
      label: labels.orderedList,
      active: editor.isActive('orderedList'),
      content: <FormatListNumbered />,
      run: () => editor.chain().focus().toggleOrderedList().run(),
    },
  ];

  return (
    <Frame>
      <Toolbar role="toolbar" aria-label={labels.toolbar}>
        {controls.map(control => (
          <ToolButton
            key={control.label}
            type="button"
            aria-label={control.label}
            aria-pressed={control.active}
            active={control.active}
            onClick={control.run}
          >
            {control.content}
          </ToolButton>
        ))}
      </Toolbar>
      <BubbleMenu editor={editor} tippyOptions={{ duration: 120 }}>
        <BubbleBar role="toolbar" aria-label={labels.selectionToolbar}>
          {controls.map(control => (
            <BubbleButton
              key={control.label}
              type="button"
              aria-label={control.label}
              aria-pressed={control.active}
              active={control.active}
              onClick={control.run}
            >
              {control.content}
            </BubbleButton>
          ))}
        </BubbleBar>
      </BubbleMenu>
      <EditArea>
        <EditorContent editor={editor} />
      </EditArea>
    </Frame>
  );
};

export default RichTextEditor;
